import CoreImage from 'core/variables/images';
import PropTypes from 'prop-types';
import React from 'react';
import useImage from './useImage';

const Image = ({
  alt = '',
  availableSizes = null,
  className = 'image',
  decorative = false,
  disableOptimization = false,
  id = 'logoBig',
  loading = 'lazy',
  onError = null,
  responsiveSizes = null,
  role = null,
  sizes = null,
  src = null,
  srcSet = null,
  usePicture = false,
  ...props
}) => {
  // If src is provided, use it directly (external image)
  // Otherwise, use the id to get internal image from CoreImage
  const imageSrc = src || CoreImage.getImage(id);

  // Usar hook para manejar lógica de optimización y fallback progresivo
  const {
    imageSrc: optimizedSrc,
    imageSrcSet,
    sizesString,
    handleImageError,
  } = useImage({
    src: imageSrc,
    srcSet,
    disableOptimization,
    responsiveSizes,
    sizes,
    availableSizes,
  });

  // Combinar handleImageError del hook con onError del usuario
  const handleError = (event) => {
    // Ejecutar handler del hook primero (incrementa maxSizeIndex)
    handleImageError();

    // Luego ejecutar handler personalizado del usuario si existe
    if (onError) {
      onError(event);
    }
  };

  // Determine if image is decorative
  // An image is decorative if:
  // 1. explicitly marked as decorative, OR
  // 2. no alt text provided AND no role provided (implicitly decorative)
  const isDecorative = decorative || (alt === '' && !role);

  // Handle alt text for accessibility
  // Decorative images should have empty alt text, otherwise use provided or generate from ID
  let altText = '';
  if (!isDecorative) {
    altText = alt || `Image: ${id}`;
  }

  // Base image props
  const imageProps = {
    className,
    src: optimizedSrc,
    alt: altText,
    loading: loading || 'lazy',
    ...(sizesString && { sizes: sizesString }),
    ...(imageSrcSet && { srcSet: imageSrcSet }),
    ...(role && { role }),
    ...(isDecorative && { 'aria-hidden': 'true' }),
    ...(handleError && { onError: handleError }),
    ...props,
  };

  // Use picture element for responsive images with multiple sources
  if (usePicture && imageSrcSet) {
    return (
      <picture className={className}>
        <source srcSet={imageSrcSet} sizes={sizesString} />
        <img {...imageProps} />
      </picture>
    );
  }

  // Use regular img element
  return <img {...imageProps} />;
};

Image.propTypes = {
  alt: PropTypes.string,
  availableSizes: PropTypes.arrayOf(PropTypes.oneOf([50, 400, 800, 1280])),
  className: PropTypes.string,
  decorative: PropTypes.bool, // For decorative images (no alt needed)
  disableOptimization: PropTypes.bool, // Disable automatic image optimization
  id: PropTypes.oneOf([
    'logoBig',
    'logoIconS',
    'noBooks',
    'noItems',
    'noUsers',
    'noQuestions',
    'noCourses',
    'barMenuLogo',
    'logoWhite',
    'noPictureProgram',
    'cover-card-task',
    'cover-card-out-of-time',
    'cover-card-quizz',
    'noMembers',
    'videoContentPreview',
    'login',
    'signup1',
    'signup2',
    'signup3',
    'signup4',
    'signup5',
    'cover-test-started',
    'cover-test-blocked',
    'cover-test-default',
    'cover-test-confirmation',
    'cover-test-formative',
    'cover-test-formative-late',
    'image-layout-progress',
    'cover-card-videolesson',
    'cover-card-audio',
    'cover-card-doc',
    'cover-card-link',
    'cover-card-video',
    'noMaterials',
    'noUnitProgram',
    'noModuleCover',
  ]),
  loading: PropTypes.oneOf(['lazy', 'eager']),
  onError: PropTypes.func, // Error handler for failed image loads
  responsiveSizes: PropTypes.arrayOf(PropTypes.string), // Custom responsive breakpoints
  role: PropTypes.string, // Custom ARIA role
  sizes: PropTypes.string, // For responsive images
  src: PropTypes.string, // External image URL
  srcSet: PropTypes.string, // For responsive images
  usePicture: PropTypes.bool, // Use picture element for responsive images
};

export default Image;
